'use client';

import { useState } from 'react';

interface Props {
  productName: string;
  configSummary: Record<string, string>;
}

export function ExportTools({ productName, configSummary }: Props) {
  const [exporting, setExporting] = useState<string | null>(null);

  async function takeScreenshot() {
    setExporting('screenshot');
    try {
      const canvas = document.querySelector('canvas') as HTMLCanvasElement;
      if (!canvas) return;

      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${productName.replace(/\s+/g, '-').toLowerCase()}-config.png`;
      link.href = dataUrl;
      link.click();
    } catch {} finally { setExporting(null); }
  }

  async function exportPDF() {
    setExporting('pdf');
    try {
      const canvas = document.querySelector('canvas') as HTMLCanvasElement;
      const imgData = canvas?.toDataURL('image/png');

      const entries = Object.entries(configSummary);
      const date = new Date().toLocaleDateString();

      const html = `
<!DOCTYPE html>
<html>
<head><title>${productName} Configuration</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; max-width: 700px; margin: 40px auto; color: #1a1a1a; }
  h1 { font-size: 24px; margin-bottom: 4px; }
  .meta { color: #888; font-size: 12px; margin-bottom: 24px; }
  img { width: 100%; max-height: 400px; object-fit: contain; border: 1px solid #eee; border-radius: 12px; margin-bottom: 24px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { text-align: left; padding: 8px 12px; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
  th { color: #888; font-weight: 500; }
  .footer { margin-top: 40px; text-align: center; color: #ccc; font-size: 10px; }
  @media print { body { margin: 20px; } }
</style>
</head>
<body>
  <h1>${productName}</h1>
  <p class="meta">Configuration Summary &middot; ${date}</p>
  ${imgData ? `<img src="${imgData}" alt="3D Preview" />` : ''}
  <table>
    <tr><th>Option</th><th>Selection</th></tr>
    ${entries.map(([k, v]) => `<tr><td>${k}</td><td><strong>${v}</strong></td></tr>`).join('\n')}
  </table>
  <div class="footer">Generated by Threekit</div>
</body>
</html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) {
        win.onload = () => {
          setTimeout(() => { win.print(); }, 500);
        };
      }
      URL.revokeObjectURL(url);
    } catch {} finally { setExporting(null); }
  }

  return (
    <div className="flex items-center gap-1.5 pt-2 border-t border-zinc-200 dark:border-zinc-800 mt-2">
      <button
        onClick={takeScreenshot}
        disabled={!!exporting}
        className="flex-1 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-800 text-[11px] font-medium text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 disabled:opacity-40 transition-colors"
      >
        {exporting === 'screenshot' ? 'Capturing...' : '📷 Screenshot'}
      </button>
      <button
        onClick={exportPDF}
        disabled={!!exporting}
        className="flex-1 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-800 text-[11px] font-medium text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 disabled:opacity-40 transition-colors"
      >
        {exporting === 'pdf' ? 'Generating...' : '📄 PDF Export'}
      </button>
    </div>
  );
}
